<template>
  <div class="sales-report-page m-left-page">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#3F033B"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />

    <v-snackbar v-model="showsnackbar" color="#3F033B" right :timeout="timeout">
      <v-layout wrap justify-center>
        <v-flex text-left class="align-self-center">
          <span style="color: white">
            {{ msg }}
          </span>
        </v-flex>

        <v-flex text-right>
          <v-btn small :ripple="false" text @click="showsnackbar = false">
            <v-icon style="color: #fff">mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>

    <!-- Header Section -->
    <v-layout wrap align-center justify-space-between class="sales-header-bar mb-5">
      <v-flex xs12 sm6 md7 text-left class="d-flex align-center">
        <div class="d-flex align-center flex-wrap">
          <span class="mainHeader">Sales Report</span>
          <span class="report-badge ml-3" v-if="totalLength !== null && totalLength > 0">
            {{ totalLength }} {{ totalLength === 1 ? 'Record' : 'Records' }}
          </span>
        </div>
      </v-flex>

      <v-flex xs12 sm6 md5 lg4 class="mt-3 mt-sm-0 d-flex justify-end flex-wrap" style="gap: 10px">
        <v-select
          v-model="typeFilter"
          :items="typeFilterOptions"
          item-text="label"
          item-value="value"
          background-color="white"
          color="#3F033B"
          hide-details
          outlined
          dense
          class="sales-type-filter rounded-xl"
        >
        </v-select>

        <v-text-field
          v-model="keyword"
          background-color="white"
          color="#3F033B"
          clearable
          hide-details
          placeholder="Search bill no, customer, mobile..."
          outlined
          class="sales-search-field rounded-xl"
          dense
          prepend-inner-icon="mdi-magnify"
        >
        </v-text-field>
      </v-flex>
    </v-layout>

    <!-- Table Card Container -->
    <v-layout wrap>
      <v-flex xs12>
        <v-card class="sales-table-card" elevation="0">
          <div class="table-responsive-container">
            <table class="refined-sales-table m-stack-table m-stack-grid">
              <thead>
                <tr>
                  <th class="th-sno">S.No.</th>
                  <th class="th-bill">Bill No</th>
                  <th class="th-date">Date</th>
                  <th class="th-customer">Customer Name</th>
                  <th class="th-contact">Contact</th>
                  <th class="th-type">Type</th>
                  <th class="th-num">Sale Amt (₹)</th>
                  <th class="th-num">Old Metal Amt (₹)</th>
                  <th class="th-num">Return Amt (₹)</th>
                  <th class="th-num th-total">Total Amt (₹)</th>
                  <th class="th-status">Status</th>
                </tr>
              </thead>

              <!-- Data Rows -->
              <tbody v-if="dataList && dataList.length > 0">
                <tr v-for="(value, i) in dataList" :key="i" class="table-row">
                  <!-- S.No -->
                  <td data-label="S.No." class="td-sno">
                    {{ (page - 1) * 20 + i + 1 }}
                  </td>

                  <!-- Bill No -->
                  <td data-label="Bill No" class="td-bill">
                    <span class="bill-badge" v-if="value._id && value._id[0]">
                      {{ (value._id[0].billPrefix || "") + (value._id[0].billNo || "") }}
                    </span>
                    <span v-else class="text-muted">—</span>
                  </td>

                  <!-- Date -->
                  <td data-label="Date" class="td-date">
                    <span v-if="value._id && value._id[0] && value._id[0].create_date">
                      {{ formatDate(value._id[0].create_date) }}
                    </span>
                    <span v-else class="text-muted">—</span>
                  </td>

                  <!-- Customer Name -->
                  <td data-label="Customer Name" class="td-customer">
                    <span class="customer-name" v-if="value._id && value._id[0] && value._id[0].customerName">
                      {{ value._id[0].customerName }}
                    </span>
                    <span v-else class="text-muted">—</span>
                  </td>

                  <!-- Contact -->
                  <td data-label="Contact" class="td-contact">
                    <span class="contact-text" v-if="value._id && value._id[0] && value._id[0].mobileNumber">
                      {{ value._id[0].mobileNumber }}
                    </span>
                    <span v-else class="text-muted">—</span>
                  </td>

                  <!-- Type -->
                  <td data-label="Type" class="td-type">
                    <span
                      v-for="tag in getRowTags(value._id && value._id[0])"
                      :key="tag"
                      class="type-chip"
                    >
                      {{ tag }}
                    </span>
                  </td>

                  <!-- Sale Amt -->
                  <td data-label="Sale Amt (₹)" class="td-num">
                    <template v-if="value._id && value._id[0] && value._id[0].saleTotal">
                      {{ formatCurrency(value._id[0].saleTotal) }}
                      <div class="td-subtext" v-if="value._id[0].goldRate">
                        @ ₹{{ formatNumber(value._id[0].goldRate) }}/g
                      </div>
                    </template>
                    <span v-else class="text-muted">—</span>
                  </td>

                  <!-- Old Metal Amt (Gold + Silver) -->
                  <td data-label="Old Metal Amt (₹)" class="td-num">
                    <template v-if="value._id && value._id[0] && ((value._id[0].oldGoldAmountGold > 0) || (value._id[0].oldGoldAmountSilver > 0))">
                      <div v-if="value._id[0].oldGoldAmountGold > 0">
                        {{ formatCurrency(value._id[0].oldGoldAmountGold) }}
                        <span class="td-subtext">Gold</span>
                      </div>
                      <div v-if="value._id[0].oldGoldAmountSilver > 0">
                        {{ formatCurrency(value._id[0].oldGoldAmountSilver) }}
                        <span class="td-subtext">Silver</span>
                      </div>
                    </template>
                    <span v-else class="text-muted">—</span>
                  </td>

                  <!-- Return Amt -->
                  <td data-label="Return Amt (₹)" class="td-num">
                    <span v-if="value._id && value._id[0] && value._id[0].returnAmount && value._id[0].returnAmount > 0">
                      {{ formatCurrency(value._id[0].returnAmount) }}
                    </span>
                    <span v-else class="text-muted">—</span>
                  </td>

                  <!-- Total Amt -->
                  <td data-label="Total Amt (₹)" class="td-num td-total">
                    <span class="total-amt-text" v-if="value._id && value._id[0]">
                      ₹ {{ formatCurrency(billTotalAmount(value._id[0])) }}
                    </span>
                    <span v-else class="text-muted">0.00</span>
                  </td>

                  <!-- Status -->
                  <td data-label="Status" class="td-status">
                    <span
                      v-if="value._id && value._id[0]"
                      class="status-pill"
                      :class="getStatusClass(value._id[0].paymentStatus)"
                    >
                      <span class="status-dot"></span>
                      {{ formatStatus(value._id[0].paymentStatus) }}
                    </span>
                    <span v-else class="text-muted">—</span>
                  </td>
                </tr>
              </tbody>

              <!-- Empty State -->
              <tbody v-else-if="!appLoading">
                <tr>
                  <td colspan="11" class="empty-state-cell">
                    <div class="empty-state-wrapper">
                      <v-icon size="46" color="#CBD5E1">mdi-receipt-text-outline</v-icon>
                      <div class="empty-state-title">No Sales Records Found</div>
                      <div class="empty-state-subtitle">
                        {{ keyword ? 'No results matched "' + keyword + '". Try a different search.' : 'No sales records are available at the moment.' }}
                      </div>
                      <v-btn
                        v-if="keyword"
                        small
                        outlined
                        color="#3F033B"
                        class="mt-3 rounded-lg text-capitalize"
                        @click="keyword = ''"
                      >
                        Clear Search
                      </v-btn>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Footer: Records Info & Pagination -->
          <div class="table-footer" v-if="dataList && dataList.length > 0">
            <div class="footer-info">
              Showing
              <span class="font-weight-bold">{{ (page - 1) * 20 + 1 }}</span>
              to
              <span class="font-weight-bold">{{ Math.min(page * 20, totalLength) }}</span>
              of
              <span class="font-weight-bold">{{ totalLength }}</span>
              records
            </div>

            <div class="footer-pagination" v-if="Pagelength > 1">
              <v-pagination
                small
                color="#3F033B"
                v-model="page"
                :length="Pagelength"
                :total-visible="7"
              >
              </v-pagination>
            </div>
          </div>
        </v-card>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 2000,
      msg: null,
      page: 1,
      Pagelength: 1,
      totalLength: 0,
      dataList: [],
      keyword: null,
      searchTimer: null,
      typeFilter: "All",
      typeFilterOptions: [
        { label: "All Types", value: "All" },
        { label: "Sales", value: "Sales" },
        { label: "Sales Return", value: "Sales Return" },
      ],
      juwellaryId: localStorage.getItem("juwellaryId"),
    };
  },
  mounted() {
    this.getData();
  },
  computed: {
    appUser() {
      return this.$store.state.userData;
    },
  },
  watch: {
    keyword() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        if (this.page !== 1) {
          this.page = 1;
        } else {
          this.getData();
        }
      }, 300);
    },
    page() {
      this.getData();
    },
    typeFilter() {
      if (this.page !== 1) {
        this.page = 1;
      } else {
        this.getData();
      }
    },
  },
  methods: {
    getData() {
      this.appLoading = true;
      axios({
        method: "get",
        url: "/sales/payment/getlist",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          keyword: this.keyword,
          page: this.page,
          limit: 20,
          jewelleryId: this.juwellaryId,
          type: this.typeFilter !== "All" ? this.typeFilter : undefined,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status == true) {
            this.dataList = response.data.payment || [];
            this.totalLength = response.data.totalLength || 0;
            this.Pagelength = Math.ceil((response.data.totalLength || 0) / 20) || 1;
          } else {
            this.showsnackbar = true;
            this.msg = response.data.msg;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          console.log(err);
        });
    },

    formatDate(item) {
      if (!item) return "—";
      var dt = new Date(item);
      if (isNaN(dt.getTime())) return "—";
      var day = dt.getDate();
      var year = dt.getFullYear();
      var months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      var month = months[dt.getMonth()];
      return (day < 10 ? "0" + day : day) + " " + month + " " + year;
    },

    formatNumber(val) {
      if (val === null || val === undefined || val === "" || isNaN(val)) return "0";
      return Number(val).toLocaleString("en-IN");
    },

    formatCurrency(val) {
      if (val === null || val === undefined || val === "" || isNaN(val)) return "0.00";
      return Number(val).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    // The bill's true payable total. `payableAmount` is not usable here - it
    // gets overwritten on every partial settlement with just that
    // installment's amount, not the bill total. `billNetAmount` (sale total
    // minus old-metal/return) is recomputed identically on every settlement
    // from figures that don't change with payment, so net of the bill
    // discount it reliably equals the full bill total regardless of how
    // many installments were paid.
    billTotalAmount(sale) {
      if (!sale) return 0;
      return (sale.billNetAmount || 0) - (sale.billDiscount || 0);
    },

    formatStatus(status) {
      if (!status || status === 0 || status === "0") return "Pending";
      return String(status).trim();
    },

    getRowTags(item) {
      if (!item) return [];
      const tags = [];
      if (item.saleTotal > 0) tags.push("Sale");
      if (item.oldGoldAmountGold > 0 || item.oldGoldAmountSilver > 0) tags.push("Old Metal");
      if (item.returnAmount > 0) tags.push("Return");
      return tags.length ? tags : ["Sale"];
    },

    getStatusClass(status) {
      if (!status) return "status-pending";
      const s = String(status).trim().toLowerCase();
      if (s === "paid" || s === "completed" || s === "success") return "status-paid";
      if (s === "pending" || s === "due" || s === "unpaid" || s === "0") return "status-pending";
      if (s === "partial" || s === "partially paid") return "status-partial";
      return "status-default";
    },
  },
};
</script>

<style scoped>
.sales-report-page {
  padding-bottom: 24px;
}

/* Header bar */
.sales-header-bar {
  padding-top: 4px;
}

.report-badge {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  color: #3F033B;
  background: rgba(63, 3, 59, 0.08);
  border: 1px solid rgba(63, 3, 59, 0.16);
  padding: 3px 10px;
  border-radius: 20px;
  letter-spacing: 0.02em;
}

/* Search Field */
.sales-search-field {
  max-width: 300px;
}

.sales-type-filter {
  max-width: 150px;
}

.sales-type-filter ::v-deep .v-input__control .v-input__slot {
  min-height: 40px !important;
  border-radius: 24px !important;
  border: 1px solid #E2E8F0 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03) !important;
  background-color: #ffffff !important;
}

.sales-type-filter ::v-deep fieldset {
  border: none !important;
}

.sales-type-filter ::v-deep .v-select__selection,
.sales-type-filter ::v-deep input {
  font-size: 13px !important;
  color: #3F033B !important;
  font-weight: 600;
}

.sales-search-field ::v-deep .v-input__control .v-input__slot {
  min-height: 40px !important;
  border-radius: 24px !important;
  border: 1px solid #E2E8F0 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03) !important;
  transition: all 0.2s ease;
  background-color: #ffffff !important;
}

.sales-search-field ::v-deep .v-input__control .v-input__slot:focus-within {
  border-color: #3F033B !important;
  box-shadow: 0 0 0 3px rgba(63, 3, 59, 0.08) !important;
}

.sales-search-field ::v-deep fieldset {
  border: none !important;
}

.sales-search-field ::v-deep input {
  font-size: 13.5px !important;
}

/* Table Card Container */
.sales-table-card {
  background: #ffffff !important;
  border-radius: 14px !important;
  border: 1px solid #EAECEF !important;
  box-shadow: 0 4px 20px -2px rgba(63, 3, 59, 0.04), 0 2px 6px -1px rgba(0, 0, 0, 0.02) !important;
  overflow: hidden;
}

/* Responsive Table Scroll */
.table-responsive-container {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.table-responsive-container::-webkit-scrollbar {
  height: 6px;
}
.table-responsive-container::-webkit-scrollbar-track {
  background: #F8FAFC;
}
.table-responsive-container::-webkit-scrollbar-thumb {
  background: #CBD5E1;
  border-radius: 4px;
}
.table-responsive-container::-webkit-scrollbar-thumb:hover {
  background: #94A3B8;
}

/* Table Base */
.refined-sales-table {
  width: 100%;
  min-width: 1020px;
  border-collapse: separate;
  border-spacing: 0;
  text-align: left;
}

/* Table Header */
.refined-sales-table thead tr {
  background: #FAF8F5;
}

.refined-sales-table th {
  padding: 14px 14px;
  font-size: 11.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #555A64;
  white-space: nowrap;
  border-bottom: 2px solid #EDE8E2;
}

/* Header Column Widths & Alignments */
.th-sno {
  width: 55px;
  text-align: center;
}
.th-bill {
  width: 95px;
  text-align: left;
}
.th-date {
  width: 115px;
  text-align: left;
}
.th-customer {
  min-width: 130px;
  text-align: left;
}
.th-contact {
  width: 115px;
  text-align: left;
}
.th-type {
  width: 130px;
  text-align: left;
}
.th-num {
  text-align: right;
  min-width: 115px;
}
.th-total {
  min-width: 125px;
  color: #3F033B !important;
}
.th-status {
  width: 110px;
  text-align: center;
}

/* Table Body Rows */
.refined-sales-table tbody tr.table-row {
  transition: background-color 0.15s ease;
  border-bottom: 1px solid #F1F3F5;
}

.refined-sales-table tbody tr.table-row:hover {
  background-color: #FAF6F0; /* Subtle warm brand tint on hover */
}

.refined-sales-table td {
  padding: 13px 14px;
  font-size: 13px;
  color: #1F2937;
  border-bottom: 1px solid #F1F3F5;
  vertical-align: middle;
}

/* Cell Alignments & Formatting */
.td-sno {
  text-align: center;
  color: #8A94A6;
  font-size: 12.5px;
  font-weight: 500;
}
.td-bill {
  text-align: left;
}
.td-date {
  text-align: left;
  white-space: nowrap;
  color: #4B5563;
  font-size: 12.5px;
}
.td-customer {
  text-align: left;
}
.td-contact {
  text-align: left;
  white-space: nowrap;
}
.td-type {
  text-align: left;
  white-space: nowrap;
}
.td-num {
  text-align: right;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  font-size: 13px;
  color: #334155;
}
.td-subtext {
  font-size: 10.5px;
  font-weight: 500;
  color: #94A3B8;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

/* Type Chip */
.type-chip {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  color: #3F033B;
  background: rgba(63, 3, 59, 0.06);
  border: 1px solid rgba(63, 3, 59, 0.14);
  padding: 2px 8px;
  border-radius: 12px;
  margin-right: 4px;
  margin-bottom: 2px;
  white-space: nowrap;
}
.td-total {
  text-align: right;
}
.td-status {
  text-align: center;
  white-space: nowrap;
}

/* Bill Badge */
.bill-badge {
  display: inline-block;
  font-weight: 600;
  font-size: 12.5px;
  color: #3F033B;
  background: rgba(63, 3, 59, 0.06);
  border: 1px solid rgba(63, 3, 59, 0.14);
  padding: 3px 8px;
  border-radius: 6px;
  letter-spacing: 0.02em;
  white-space: nowrap;
}

.customer-name {
  font-weight: 600;
  color: #111827;
  text-transform: capitalize;
  letter-spacing: 0.01em;
}

.contact-text {
  color: #64748B;
  font-size: 12.5px;
  letter-spacing: 0.02em;
}

.total-amt-text {
  font-weight: 700;
  color: #3F033B;
  font-size: 13.5px;
  font-variant-numeric: tabular-nums;
}

.text-muted {
  color: #94A3B8;
}

/* Status Chips */
.status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.02em;
  line-height: 1;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-paid {
  background-color: #ECFDF5;
  color: #065F46;
  border: 1px solid #A7F3D0;
}
.status-paid .status-dot {
  background-color: #10B981;
}

.status-pending {
  background-color: #FFFBEB;
  color: #92400E;
  border: 1px solid #FDE68A;
}
.status-pending .status-dot {
  background-color: #F59E0B;
}

.status-partial {
  background-color: #EFF6FF;
  color: #1E40AF;
  border: 1px solid #BFDBFE;
}
.status-partial .status-dot {
  background-color: #3B82F6;
}

.status-default {
  background-color: #F1F5F9;
  color: #475569;
  border: 1px solid #E2E8F0;
}
.status-default .status-dot {
  background-color: #94A3B8;
}

/* Empty State */
.empty-state-cell {
  padding: 48px 24px !important;
  text-align: center;
}
.empty-state-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.empty-state-title {
  font-size: 16px;
  font-weight: 600;
  color: #475569;
  margin-top: 12px;
}
.empty-state-subtitle {
  font-size: 13px;
  color: #94A3B8;
  margin-top: 4px;
}

/* Table Footer */
.table-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 14px 20px;
  border-top: 1px solid #EDE8E2;
  background: #FAF8F5;
}
.footer-info {
  font-size: 13px;
  color: #64748B;
}
.footer-info span {
  color: #1F2937;
}
.footer-pagination {
  margin-left: auto;
}
</style>